/**
 * 前端配置 metricsList 覆盖后端 selector.metrics_list 的公共 Hook
 *
 * 背景：部分页面（如 casual 框架的事件对比）支持通过 page config 的
 * `filters[].metricsList` 在前端覆盖后端返回的 metrics 列表，并支持
 * `{{metricsInfo.xxx.en}}` / `{{metricsInfo.xxx.cn}}` 模板写法。
 *
 * 同步回 redux 原因：`NewFilter.onChange → getDefaultValueFromSaved →
 * getfilterOptionsValue` 仍会读 redux 里的后端 metrics_list 做"二次校验"，
 * 把前端独有的 metric 从已选值中剔除，造成"勾不上"。
 */

import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'umi';
import { isEqual } from 'lodash-es';
import { dynamicString } from '@/pages/home/utils/dynamicString';

/** page config 中 `filters[].metricsList` 的单项结构 */
export interface IFrontMetricItem {
  metric: string;
  metric_name_en?: string;
  metric_name_ch?: string;
  [key: string]: unknown;
}

export function useFrontMetricsOverride(
  config: { metricsList?: IFrontMetricItem[] } | undefined,
  selector: any,
  metricsInfo: Record<string, unknown>,
) {
  const frontMetricsList = useMemo<IFrontMetricItem[]>(() => {
    const arr = Array.isArray(config?.metricsList) ? config!.metricsList! : [];
    if (arr.length === 0) return arr;
    const ds = { metricsInfo };
    const resolve = (v: unknown) =>
      typeof v === 'string' && v.includes('{{') ? dynamicString(v, ds, '') || v : v;
    return arr.map((it) => ({
      ...it,
      metric_name_en: resolve(it?.metric_name_en) as string | undefined,
      metric_name_ch: resolve(it?.metric_name_ch) as string | undefined,
    }));
  }, [config?.metricsList, metricsInfo]);

  const useFrontMetrics = frontMetricsList.length > 0;

  const mergedSelector = useMemo(
    () => (useFrontMetrics ? { ...(selector || {}), metrics_list: frontMetricsList } : selector),
    [selector, useFrontMetrics, frontMetricsList],
  );

  const dispatch = useDispatch();
  const lastSyncedRef = useRef<IFrontMetricItem[] | null>(null);
  useEffect(() => {
    if (!useFrontMetrics || !selector?.gameCode) return;
    if (isEqual(selector.metrics_list, frontMetricsList)) return;
    if (isEqual(lastSyncedRef.current, frontMetricsList)) return;
    lastSyncedRef.current = frontMetricsList;
    dispatch({
      type: 'dashboardSetting/save',
      payload: { selector: { ...selector, metrics_list: frontMetricsList } },
    });
  }, [selector, useFrontMetrics, frontMetricsList, dispatch]);

  return { mergedSelector, frontMetricsList, useFrontMetrics };
}
